import { snakeCase } from 'lodash'
import { editableFilterTypes } from 'shared/column-filters/FilterEditor'
import { makeGetColumnOptions } from 'shared/column-filters/getColumnOptions'
import { accountCodeType } from 'shared/column-filters/AccountCodePicker'
import { ANALYTIC_FLAG_LABELS } from 'shared/column-filters/AnalyticFlagFilterPopover'
import {
  customColumnFilterColumn,
  customColumnFilterOptions,
  filterableCustomColumnTypes
} from 'shared/column-filters/custom-column-filter'
import { leafFilter } from 'api/filter-tree'
import { getColumnDisplayName, groupColumnsForDisplay, isAccountSegmentColumn } from 'dashboard/utils'
import { choiceVerb, summarizeClauses } from './summary'

// Reference columns get record editors of their own below (vendors, employees): the grid's reference
// picker saves to the View's dashboard_vendors/dashboard_employees rows, which a group can't hold.
const TREE_FILTER_TYPES = editableFilterTypes.filter((type) => type !== 'reference')

// The reference columns a leaf can filter: the transaction's vendor, and the detail columns that
// point at an employee (Entered By, Cardholder, Approver…). The requisition vendor columns are not
// the transaction's vendor, so they stay out.
const isVendorColumn = (column) => column.filter_type === 'reference' && column.database_column_name === 'vendor_name'
const isEmployeeColumn = (column) =>
  column.filter_type === 'reference' &&
  /_id$/.test(column.database_column_name || '') &&
  !/vendor/.test(column.database_column_name)

// A vendor or employee leaf keeps its picks as { selections: [{ id, label }], operator }.
const selectionsSummary = ({ ui = {} }) => {
  const labels = (ui.selections || []).map(({ label }) => label)
  return labels.length ? summarizeClauses([{ verb: choiceVerb(labels.length, ui.operator === 'exclude'), values: labels }]) : ''
}

const selectedIds = (ui) => (ui.selections || []).map(({ id }) => id)

const recordColumn = (column, filterType, sectionId, filterFor) => ({
  column: {
    ...column,
    accessor_key: column.accessor_key || column.database_column_name,
    section_id: sectionId,
    filter_type: filterType,
    summarizeLeaf: selectionsSummary
  },
  options: [],
  filterFor
})

const vendorColumn = (column, sectionId) =>
  recordColumn(column, 'vendor', sectionId, (ui) => {
    const ids = selectedIds(ui)
    return ids.length ? { [ui.operator === 'exclude' ? 'excluded_vendors' : 'vendors']: ids.join('+') } : {}
  })

// An employee leaf keeps EmployeeSearch's staged picks: { employees, excludedEmployees }, each
// [{ id, display_name, database_column_name, options }]. Leaves saved before that kept selections.
const employeesIn = (records, column) =>
  (records || []).filter((record) => !record.database_column_name || record.database_column_name === column)

const employeeSummary = (columnName) => (leaf) => {
  const { ui = {} } = leaf
  if (ui.selections) return selectionsSummary(leaf)
  const name = (employee) => employee.display_name || employee.full_name || employee.id
  const included = employeesIn(ui.employees, columnName).map(name)
  const excluded = employeesIn(ui.excludedEmployees, columnName).map(name)
  return summarizeClauses([
    included.length && { verb: choiceVerb(included.length), values: included },
    excluded.length && { verb: choiceVerb(excluded.length, true), values: excluded }
  ].filter(Boolean))
}

const employeeColumn = (column, sectionId) => {
  const columnName = column.database_column_name
  const entry = recordColumn(column, 'employee', sectionId, (ui) => {
    const ids = (records) => employeesIn(records, columnName).map(({ id }) => id).join('+')
    const filter = { employee_column: columnName, employee_ids: ids(ui.employees), excluded_employee_ids: ids(ui.excludedEmployees) }
    return filter.employee_ids || filter.excluded_employee_ids ? filter : {}
  })
  return { ...entry, column: { ...entry.column, summarizeLeaf: employeeSummary(columnName) } }
}

// An account segment saves code ids under <segment>_ids, as the grid header does; the names the
// editor kept alongside make its summary readable. Segment columns carry no accessor_key, so a
// leaf names them by database_column_name, as the grid does.
const accountSegmentColumn = (column, municipality) => {
  const accountType = column.database_column_name
  const codeType = accountCodeType(accountType)
  const idsKey = `${accountType}_ids`
  const operatorKey = `${snakeCase(codeType)}_operator`
  return {
    column: {
      ...column,
      accessor_key: accountType,
      display_name: getColumnDisplayName(column, municipality),
      filter_type: 'account_segment',
      summarizeLeaf: ({ filter = {}, ui = {} }) => {
        const ids = String(filter[idsKey] || '').split('+').filter(Boolean)
        if (!ids.length) return ''
        const labels = ids.map((id) => {
          const label = ui.accountCodeLabels?.[id]
          return label ? [label.name, label.code].filter(Boolean).join(' ') : id
        })
        return summarizeClauses([{ verb: choiceVerb(ids.length, filter[operatorKey] === 'exclude'), values: labels }])
      }
    },
    options: [],
    filterFor: (ui) => {
      const ids = ui[codeType] || []
      return ids.length ? { [idsKey]: ids.join('+'), [operatorKey]: ui[`${codeType}Operator`] || 'include' } : {}
    }
  }
}

// One per analytic in the View's module: whether it holds an unresolved flag from it.
const analyticColumn = (analytic) => ({
  column: {
    accessor_key: `analytic_${analytic.id}`,
    name: analytic.name,
    description: analytic.description,
    column_sources: analytic.column_sources,
    filter_type: 'analytic_flag',
    summarizeLeaf: ({ filter = {} }) => ANALYTIC_FLAG_LABELS[filter.analytics?.[0]?.options] || ''
  },
  options: [],
  group: 'Analytics',
  filterFor: (ui) => (ui.analyticFlag ? { analytics: [{ id: analytic.id, options: ui.analyticFlag }] } : {})
})

// Table columns under their grouping (Invoice, Vendor, Account…), ordered as the View's column
// picker orders them.
const byGrouping = (entries, municipality) => {
  const entryFor = new Map(entries.map((entry) => [entry.column, entry]))
  return groupColumnsForDisplay(entries.map(({ column }) => column), municipality).flatMap(({ label, columns }) =>
    columns.map((column) => ({ ...entryFor.get(column), group: label }))
  )
}

// The columns a filter in a group can target, each with its editor's options, the heading it is
// listed under and the serializer that turns its editor state into the params a request sends.
export const buildFilterColumns = ({ columns = [], customColumns = [], analytics = [], municipality = null, sectionId = null }) => {
  const getOptions = makeGetColumnOptions(columns, municipality)
  const tableColumns = [
    ...columns
      .filter((column) => column.accessor_key && TREE_FILTER_TYPES.includes(column.filter_type))
      .map((column) => ({
        column,
        options: getOptions(column.accessor_key),
        filterFor: (ui) => leafFilter(ui, { columns: [column] })
      })),
    ...columns.filter(isAccountSegmentColumn).map((column) => accountSegmentColumn(column, municipality)),
    ...columns.filter(isVendorColumn).map((column) => vendorColumn(column, sectionId)),
    ...columns.filter(isEmployeeColumn).map((column) => employeeColumn(column, sectionId))
  ]
  return [
    ...byGrouping(tableColumns, municipality),
    ...customColumns
      .filter((customColumn) => filterableCustomColumnTypes.includes(customColumn.column_type))
      .map((customColumn) => ({
        column: customColumnFilterColumn(customColumn),
        options: customColumnFilterOptions(customColumn),
        group: 'Custom columns',
        filterFor: (ui) => leafFilter(ui, { customColumns: [customColumn] })
      })),
    ...analytics.map(analyticColumn)
  ]
}
